import React, { useEffect, useState } from 'react';
import { ArrowRight, Files, Eye, CalendarCheck } from '@phosphor-icons/react';
import Text, { TTextColor } from '../../../../components/atoms/Text';
import PrimaryButton from '../../../../components/atoms/Button';
import {
	Deductible,
	FftItem,
	Partiality,
} from '../../../../@types/FFT.type';
import StatusIcon from './StatusIcon';
import {
	OPERATION_TYPES,
	REGIMES,
	STATUS_FFT,
} from '../../../../shared/constants/fiscalFlowTransaction';
import DetailModal from './DetailModal';
import { getDeductibleResumeByFFTId } from '../../../../services/fft.service';
import ROUTES from '../../../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import { getUserDeclarationDetailFFT } from '../../../../services/flowApproval.service';
import { ApprovalDeclarationDetailFFT } from '../../../../@types/Declarations.type';
import Modal from '../../../../components/molecules/Modal/Modal';
import { DownloadSimple } from '@phosphor-icons/react';

interface CardProps {
	regime: string;
	item: FftItem;
	monthName: string;
	year: string;
	handleDownload: (file: string) => void;
}

const StatusAndIcon: React.FC<{
	regime: string;
	item: CardProps['item'];
	getIcon: (period: string) => JSX.Element;
}> = ({ regime, item, getIcon }) => {
	const getStatusColor = (item: FftItem) => {
		if (item.is_manual && item.status !== 'completed') {
			return 'red';
		}
		switch (item.status) {
			case 'completed':
				return regime === 'annual' ? 'primary' : 'success';
			case 'pending':
				return 'gray';
			case 'missing':
				return 'gray';
			case 'in_progress':
			case 'closed':
				return 'primary';
			default:
				return 'dark';
		}
	};

	const statusColor = getStatusColor(item);

	const getStatusText = () => {
		if (item.is_manual && item.status !== 'completed') {
			return 'En progreso';
		}
		switch (item?.status) {
			case 'completed':
				return 'Finalizado';
			case 'pending':
				return 'Pendiente';
			case 'missing':
				return 'Comprar';
			case 'in_progress':
			case 'closed':
				return 'En progreso';
			default:
				return '';
		}
	};

	return (
		<div className='hidden lg:inline-flex self-stretch flex-col justify-center items-center gap-2'>
			<Text
				size={regime === 'annual' ? 'caption' : 'mini-1'}
				weight='regular'
				sx='whitespace-nowrap'
				color={statusColor as TTextColor}
			>
				{getStatusText()}
			</Text>
			{getIcon(item?.period)}
		</div>
	);
};

const ServiceInfo: React.FC<{
	regime: string;
	showDetail: boolean;
	item: CardProps['item'];
	monthName: string;
	year: string;
	analysisData: Deductible[];
	isLoadingAnalysis: boolean;
}> = ({
	regime,
	showDetail,
	item,
	monthName,
	year,
	analysisData,
	isLoadingAnalysis,
}) => {
	const [isModalOpen, setIsModalOpen] = React.useState(false);

	return (
		<div
			className={`flex items-start  w-full ${
				item?.status === STATUS_FFT.COMPLETED ? 'justify-between' : ''
			}`}
		>
			<div className='flex flex-col items-start'>
				<Text
					size={regime === 'annual' ? 'body-3' : 'caption'}
					weight={regime === 'annual' ? 'bold' : 'medium'}
					sx='!text-[#080d30]'
				>
					{item?.service}
				</Text>
				{regime !== 'annual' && (
					<Text
						size='mini-1'
						weight={item?.status === 'completed' ? 'regular' : 'light'}
						sx={`${item?.status === 'completed' ? '!text-[#080d30] mb-1' : '!text-[#080d30]'}`}
					>
						{monthName} {year}
					</Text>
				)}
			</div>

			{showDetail && (
				<>
					<div
						className='flex items-center gap-0.5 cursor-pointer'
						onClick={() => setIsModalOpen(true)}
					>
						<Text
							size='caption'
							weight='light'
							color='blue'
						>
							Ver detalle
						</Text>
						<ArrowRight
							size={16}
							className='text-[#1790ec]'
						/>
					</div>
					<DetailModal
						isOpen={isModalOpen}
						onClose={() => setIsModalOpen(false)}
						analysisData={analysisData}
						isLoadingAnalysis={isLoadingAnalysis}
						item={item}
					/>
				</>
			)}
		</div>
	);
};

const AdditionalContent: React.FC<{
	item: CardProps['item'];
	monthName: string;
	year: string;
	getIcon: (period: string) => JSX.Element;
	regime: string;
}> = ({ item, monthName, year, getIcon, regime }) => (
	<div className='grow shrink basis-0 h-auto flex justify-start items-center gap-2.5'>
		<div className='grow shrink basis-0 flex flex-col justify-center items-start gap-1'>
			<div className='self-stretch flex justify-between items-center lg:hidden'>
				<div className='flex items-center gap-2.5 flex-grow'>
					{getIcon(item?.period)}
					<Text
						size={regime === 'annual' ? 'body-3' : 'caption'}
						weight={regime === 'annual' ? 'medium' : 'regular'}
						color='medium'
						sx='line-clamp-2 overflow-hidden'
					>
						{item?.service}
					</Text>
				</div>
				{regime !== 'annual' && (
					<Text
						size='caption'
						weight='light'
						color='darkBlue'
						sx='whitespace-nowrap ml-2 shrink-0'
					>
						{monthName.slice(0, 3)} {year}
					</Text>
				)}
			</div>
		</div>
	</div>
);

const StatusMessage: React.FC<{
	item: FftItem;
	regime: string;
}> = ({ item, regime }) => (
	<div
		className={`rounded ${
			item.is_manual && item.status !== 'completed'
				? 'border-[#da1e28] border-2 bg-[#fcf5f4] p-2'
				: ''
		}`}
	>
		<Text
			color={item.is_manual && item.status !== 'completed' ? 'red' : 'medium'}
			size={regime === 'annual' ? 'caption' : 'mini-1'}
			weight='light'
		>
			{item.message?.description}
		</Text>
	</div>
);

const DownloadButtons: React.FC<{
	item: CardProps['item'];

	setIsModalDownload: (isModalDownload: boolean) => void;
}> = ({ item, setIsModalDownload }) => {
	if (item?.status !== 'completed') return null;

	if ('additional_information' in item) {
		return (
			<div className='self-stretch flex justify-start items-center gap-2.5'>
				{((item.additional_information &&
					item.additional_information?.normal) ||
					(item.additional_information &&
						item.additional_information?.detail)) && (
					<div className='flex flex-col gap-2 w-full mt-2'>
						<PrimaryButton
							variant='secondary'
							label='Descargar declaración'
							onClick={(e) => {
								e.stopPropagation();
								setIsModalDownload(true);
							}}
							sx='w-full px-4'
							beforeIcon={
								<Files
									size={16}
									color='#1D8FF3'
								/>
							}
						/>
					</div>
				)}
			</div>
		);
	}

	return null;
};

const CardFft: React.FC<CardProps> = ({
	item,
	monthName,
	year,
	handleDownload,
	regime,
}) => {
	const navigate = useNavigate();
	const [analysisData, setAnalysisData] = useState<Deductible[]>([]);
	const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
	const [declarationDetailFFT, setDeclarationDetailFFT] =
		useState<ApprovalDeclarationDetailFFT>();
	const [isModalDownload, setIsModalDownload] = useState(false);
	const shouldMoveServiceInfo = [
		'pending',
		'missing',
		'in_progress',
		'closed',
	].includes(item?.status || '');

	const isCreatedAfterApril15 = () => {
		if (!item.created_at) return false;

		const createdDate = new Date(item.created_at);
		const april15 = new Date(2025, 3, 15);

		return createdDate >= april15;
	};

	const getIcon = () => {
		if (item.is_manual && item.status !== 'completed') {
			return <StatusIcon status='blocked' />;
		}
		switch (item?.status) {
			case 'completed':
				return regime === 'annual' ? (
					<StatusIcon
						status='completed'
						annual={true}
					/>
				) : (
					<StatusIcon status='completed' />
				);
			case 'pending':
				return <StatusIcon status='pending' />;
			case 'in_progress':
				return <StatusIcon status='in_progress' />;
			case 'closed':
				return <StatusIcon status='closed' />;
			default:
				return <></>;
		}
	};

	const getTextColor = (item: FftItem) => {
		if (item.is_manual && item.status !== 'completed') {
			return 'text-h-red-50';
		} else if (item.message?.status === 'completed') {
			return regime === 'annual' ? 'text-h-primary-50' : 'text-h-success-50';
		} else if (
			item.message?.status === 'in_progress' ||
			item.message?.status === 'closed'
		) {
			return 'text-h-information-50';
		}
		return 'text-h-text-medium';
	};

	const fetchAnalysisData = async () => {
		if (!item?.id) return;

		setIsLoadingAnalysis(true);
		try {
			const response = await getDeductibleResumeByFFTId(item.id);
			if (
				response?.resource?.statusCode === 500 ||
				!Array.isArray(response?.resource)
			) {
				setAnalysisData([]);
				return;
			}
			setAnalysisData(response?.resource);
		} catch (error) {
			setAnalysisData([]);
		} finally {
			setIsLoadingAnalysis(false);
		}
	};

	const showDetail = (() => {
		return (
			item?.status === STATUS_FFT.COMPLETED &&
			regime === REGIMES.PLATEC &&
			item.operation_type === OPERATION_TYPES.MONTHLY &&
			item.id !== undefined &&
			item.id !== null
		);
	})();

	// Check if message title matches the review text
	const isReviewInProgress = [
		'Revisión de declaración en curso',
		'Cálculo de tu declaración listo',
	].includes(item?.message?.title);

	const getDeclarationDetailFFT = async (fftId: number) => {
		try {
			const response = await getUserDeclarationDetailFFT(fftId);
			setDeclarationDetailFFT(response);
		} catch (error) {}
	};

	const handleNavigate = () => {
		if (
			declarationDetailFFT?.status === 'approved' ||
			declarationDetailFFT?.status === 'automatic'
		) {
			navigate(ROUTES.DETAIL_APPROVAL + `?fftId=${item?.id}&viewLucy=true`);
		} else if (declarationDetailFFT?.status === 'denied') {
			navigate(ROUTES.DENIED_DECLARATIONS + `?fftId=${item?.id}&viewLucy=true`);
		} else {
			navigate(
				ROUTES.APPROVAL_DECLARATIONS + `?fftId=${item?.id}&viewLucy=true`,
			);
		}
	};

	useEffect(() => {
		if (item?.id && isReviewInProgress) {
			getDeclarationDetailFFT(Number(item?.id));
		}
	}, [item?.id, isReviewInProgress]);

	useEffect(() => {
		fetchAnalysisData();
	}, [item?.id]);

	return (
		<div className='w-full h-full flex items-stretch'>
			<Modal
				open={isModalDownload}
				onClose={() => setIsModalDownload(false)}
				sx='w-[90%] max-w-[500px] !p-4'
			>
				<div className='flex flex-col gap-4'>
					<Text
						size='subtitle'
						weight='bold'
						color='medium'
						sx='mb-2'
					>
						Descargar declaración
					</Text>

					<Text
						size='body-3'
						weight='regular'
						color='medium'
						sx='mb-4'
					>
						Selecciona los documentos que deseas descargar:
					</Text>

					<div className='flex flex-col gap-3'>
						{/* Botones principales de descarga */}
						{item.additional_information &&
							item.additional_information?.normal && (
								<button
									onClick={() => {
										handleDownload(
											item.additional_information?.normal || '',
										);
										setIsModalDownload(false);
									}}
									className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
								>
									<div className='flex items-center gap-3'>
										<Files
											size={20}
											weight='fill'
											className='text-h-primary'
										/>
										<Text
											size='body-3'
											weight='medium'
										>
											{regime === 'annual'
												? 'Declaración anual'
												: 'Declaración mensual'}
										</Text>
									</div>
									<DownloadSimple
										size={18}
										weight='bold'
										className='text-h-primary'
									/>
								</button>
							)}

						{item.additional_information &&
							item.additional_information?.detail && (
								<button
									onClick={() => {
										handleDownload(
											item.additional_information?.detail || '',
										);
										setIsModalDownload(false);
									}}
									className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
								>
									<div className='flex items-center gap-3'>
										<Files
											size={20}
											weight='fill'
											className='text-h-primary'
										/>
										<Text
											size='body-3'
											weight='medium'
										>
											Detalle de declaración
										</Text>
									</div>
									<DownloadSimple
										size={18}
										weight='bold'
										className='text-h-primary'
									/>
								</button>
							)}

						{item.additional_information &&
							item.additional_information?.paid && (
								<button
									onClick={() => {
										handleDownload(item.additional_information?.paid || '');
										setIsModalDownload(false);
									}}
									className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
								>
									<div className='flex items-center gap-3'>
										<Files
											size={20}
											weight='fill'
											className='text-h-primary'
										/>
										<Text
											size='body-3'
											weight='medium'
										>
											Declaración pagada
										</Text>
									</div>
									<DownloadSimple
										size={18}
										weight='bold'
										className='text-h-primary'
									/>
								</button>
							)}

						{/* Sección para pagos en parcialidades */}
						{item.additional_information &&
							item.additional_information.partialities?.length > 0 && (
								<div className='mt-2'>
									<Text
										size='body-3'
										weight='medium'
										sx='mb-4'
									>
										Pagos en parcialidades
									</Text>
									<div className='flex flex-col gap-2'>
										{item.additional_information.partialities.map(
											(installment: Partiality) => (
												<button
													key={installment.partiality_number}
													onClick={() => {
														handleDownload(installment.url);
														setIsModalDownload(false);
													}}
													className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
												>
													<div className='flex items-center gap-3'>
														<Files
															size={20}
															weight='fill'
															className='text-h-primary'
														/>
														<Text
															size='body-3'
															weight='medium'
														>
															Parcialidad {installment.partiality_number}
														</Text>
													</div>
													<DownloadSimple
														size={18}
														weight='bold'
														className='text-h-primary'
													/>
												</button>
											),
										)}
									</div>
								</div>
							)}
					</div>
				</div>
			</Modal>
			<div
				className={`w-full rounded-lg border flex justify-between items-center lg:cursor-default flex-col 
					${item.is_manual && item.status !== 'completed' ? 'border-[#da1e28] bg-[#fcf5f4]' : ''}
					${showDetail ? 'cursor-pointer' : ''}
					${regime === 'annual' ? 'bg-[#F4F9FE] border-[#2F85C6]' : 'lg:bg-white lg:border-[#e6e6e6]'}`}
				onClick={() => {
					if (window.innerWidth < 1024 && showDetail) {
						navigate(
							`${ROUTES.DETAILS_FFT}?id=${item.id}&period=${item.period}&operation_type=${item.operation_type}`,
						);
					}
				}}
			>
				{regime === 'annual' &&
					item.period === '2025-4' &&
					item.status !== 'completed' &&
					isCreatedAfterApril15() && (
						<div className='w-full px-4 py-2 bg-[#E09D28] rounded-t-lg flex justify-start items-center gap-2.5'>
							<div>
								<CalendarCheck
									size={20}
									weight='fill'
									color='white'
								/>
							</div>
							<Text
								size='body-3'
								color='white'
							>
								Ya que compraste tu declaración anual en los últimos días de
								abril, esta será presentada en el mes de mayo. ¡No te preocupes,
								estamos trabajando en ello!
							</Text>
						</div>
					)}
				<div
					className={`w-full p-2 lg:px-4 flex flex-col ${regime === 'annual' ? 'md:flex-row justify-between' : ''}`}
				>
					<div>
						{shouldMoveServiceInfo && (
							<div className='mb-2 hidden lg:block'>
								<ServiceInfo
									item={item}
									monthName={monthName}
									year={year}
									analysisData={analysisData}
									isLoadingAnalysis={isLoadingAnalysis}
									showDetail={showDetail}
									regime={regime}
								/>
							</div>
						)}
						<div className='flex items-center justify-between'>
							<StatusAndIcon
								item={item}
								getIcon={getIcon}
								regime={regime}
							/>
							<div className='flex-col hidden lg:flex flex-grow w-full ml-2'>
								{!shouldMoveServiceInfo && (
									<ServiceInfo
										item={item}
										monthName={monthName}
										year={year}
										analysisData={analysisData}
										isLoadingAnalysis={isLoadingAnalysis}
										showDetail={showDetail}
										regime={regime}
									/>
								)}
								<StatusMessage
									item={item}
									regime={regime}
								/>
							</div>
							<AdditionalContent
								item={item}
								monthName={monthName}
								year={year}
								getIcon={getIcon}
								regime={regime}
							/>
						</div>
						<div className='flex lg:hidden items-center gap-3 mt-2'>
							<span className='font-light text-sm'>
								<span
									className={`text-h-primary-40 ${getTextColor(item)} font-medium`}
								>
									{item?.status === 'completed'
										? 'Finalizado: '
										: item?.status === 'pending'
											? 'Pendiente: '
											: 'En progreso: '}
								</span>
								<span
									className={`${item.is_manual && item.status !== 'completed' ? 'text-h-red-50' : ''}`}
								>
									{` ${item?.message?.description}  `}
								</span>
							</span>
						</div>
					</div>
					{isReviewInProgress ? (
						<div className='self-stretch flex justify-start items-center gap-2.5'>
							<PrimaryButton
								variant='secondary'
								label='Ver revisión'
								onClick={(e) => {
									e.stopPropagation();
									handleNavigate();
								}}
								sx='w-full px-4 mt-2 !p-[8px]'
								labelSize='xs'
								beforeIcon={
									<Eye
										size={16}
										color='#1D8FF3'
									/>
								}
							/>
						</div>
					) : item?.status === 'completed' ? (
						<DownloadButtons
							item={item}
							setIsModalDownload={setIsModalDownload}
						/>
					) : (
						<div className='mt-auto'></div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CardFft;
